import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { SketchPicker } from "react-color";
import { HTTP } from "../../api/resources";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import UploadButton from "../Uploader/UploadButton";
import { all as translations } from "../../modules/translations/translations";
import InputModal from "../Helpers/Modal/InputModal";
import DomainSettingsInfoModal from "../Helpers/Modal/InfoModals/DomainSettingsInfoModal";

export default function General(props) {
  const [team, setTeam] = useState(props.team);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('primary');
  const [myPrimaryColor, setMyPrimaryColor] = useState(team.primary_theme);
  const [mySecondaryColor, setMySecondaryColor] = useState(team.secondary_theme);
  const [openDonors, setOpenDonors] = useState(false)
  const [openBeneficiaries, setOpenBeneficiaries] = useState(false)
  const [donorContent, setDonorContent] = useState(`Invite All ${team.donor_title}s`)
  const [beneficiaryContent, setBeneficiaryContent] = useState(`Invite All ${team.beneficiary_title}s`)
  const [beneficiaryFlag, setBeneficiaryFlag] = useState(false)
  const [openInstructions, setOpenInstructions] = useState(false)
  const [domain, setDomain] = useState(props.domain_settings)
  const [openGroups, setOpenGroups] = useState(false)
  const featureFlags = props.feature_flags
  const users = props.users
  const customDomainFlag = props.custom_domain

  useEffect(() => {
    if (team.beneficiary_title == 'Beneficiary') {
      setBeneficiaryContent('Invite All Beneficiaries')
    }

    team.feature_flags.map(flag => {
      if (flag == 'beneficiary_logins') {
        setBeneficiaryFlag(true)
        return
      }
    })
  }, [])

  const decimalToHex = (alpha) => {
    let aHex = Math.round(255 * alpha).toString(16);
    return alpha === 0 ? '00' : aHex.length < 2 ? `0${aHex}` : aHex;
  }

  const handleColorChange = (color) => {
    const hexCode = `${color.hex}${decimalToHex(color?.rgb?.a ?? defaultAlpha)}`;
    if (activeTab === 'primary') {
      setMyPrimaryColor((hexCode))
      setTeam({...team, primary_theme: hexCode})
    }

    if (activeTab === 'secondary') {
      setMySecondaryColor((hexCode))
      setTeam({...team, secondary_theme: hexCode})
    }

    if (activeTab === 'menu_color') {
      setTeam({...team, menu_text_color: hexCode})
    }
  }

  const setLogoImage = (blob) => {
    setTeam(team => {
      return {
        ...team,
        logo: blob.signed_id,
        logo_url: blob.cdn_url
      }
    });
  }

  const handleSubmit = () => {
    if (!validation()) return

    setSaving(true)
    HTTP.patch(`/settings`, {
      team: team,
      domain_settings: domain,
    }).then(response => {
      Alert.show("notice", "Settings saved!");
      setTeam(response.data);
      setSaving(false);
      window.location.reload()
    }).catch(e => {
      Alert.show("error", "Could not save. Try again or refresh.");
      setSaving(false);
    })
  }

  const validation = () => {
    if (domain.name && domain.name.includes(' ')) {
      Alert.show('notice', 'Custom domain cannot include spaces')
      return false
    }

    if (domain.name && (domain.name.includes('http:') || domain.name.includes('https:'))) {
      Alert.show('notice', 'No protocol needed, just enter the domain e.g subdomain.domain.com')
      return false
    }

    let regex = /^([a-zA-Z0-9-]+)\.([a-zA-Z0-9-]+\.[a-zA-Z]{2,})$/
    if (domain.name && !regex.test(domain.name)) {
      Alert.show('notice', 'A subdomain is required e.g subdomain.domain.com')
      return false
    }

    if (domain.name && domain.name.toLowerCase().includes('giveclarity.co')) {
      Alert.show('notice', 'You cannot use giveclarity.co as your custom domain')
      return false
    }

    return true
  }

  const handleInvite = async (message, userType, group = null) => {
     setSaving(true)

     let data = {
      group_id: group ? group.id : null,
      message: message,
      type: userType,
     }

     try{
      let response = await sendInvites(data)
      setOpenBeneficiaries(false)
      setOpenDonors(false)
      setOpenGroups(false)
      setSaving(false)
      Alert.show('notice', response.data)
     } catch {
      setSaving(false)
      Alert.show('error', 'Operation failed, please try again')
     }
  }

  const sendInvites = (data) => {
    return HTTP.post(`/settings/mass_invite`, data)
  }

  const handleDonors = () => {
    let donors = users.filter(user => user.user_type == 'donor')

    if (donors.length < 1) {
      Alert.show('notice', `You do not have any ${team.donor_title.toLowerCase()}`)
      return
    }

    let withEmail = donors.filter(donor => donor.email.length > 4)
    if (withEmail.length < 1) {
      Alert.show('notice', `None of your ${team.donor_title.toLowerCase()} have an email`)
      return
    }

    let notAccepted = withEmail.filter(donor => !donor.invitation_accepted_at)
    if (notAccepted < 1) {
      Alert.show('notice', `All ${team.donor_title.toLowerCase()}(s) with email have accepted their invitations`)
      return
    }

    setOpenDonors(true)
  }

  const handleBeneficiaries = () => {
    let beneficiaries = users.filter(user => user.user_type == 'beneficiary')

    if (beneficiaries.length < 1) {
      Alert.show('notice', `You do not have any ${team.beneficiary_title.toLowerCase()}`)
      return
    }

    let withEmail = beneficiaries.filter(beneficiary => beneficiary.email.length > 4)
    if (withEmail.length < 1) {
      Alert.show('notice', `None of your ${team.beneficiary_title.toLowerCase()} have an email`)
      return
    }

    let notAccepted = withEmail.filter(beneficiary => !beneficiary.invitation_accepted_at)
    if (notAccepted < 1) {
      Alert.show('notice', `All ${team.beneficiary_title.toLowerCase()}(s) with email have accepted their invitations`)
      return
    }

    setOpenBeneficiaries(true)
  }

  const handleGroups = () => {
    if (!props.groups || props.groups.length < 1) {
      Alert.show('notice', 'You do not have any group')
      return 
    }

    setOpenGroups(true)
  }

  const handleShowInstructions = () => {
    setOpenInstructions(true)
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(domain.dns_target)
    Alert.show('notice', 'Copied DNS Target to clipboard')
  }


  return (
    <div>
      <div className="card">
        <div className="card-header flex-header">
          <div>
            <h4 className="card-title">
              General Settings
            </h4>
          </div>
          <div>
            <button onClick={handleSubmit} name="button" className="btn btn-primary">
              {saving && <i className="fa fa-spinner-third fa-spin mr-2"></i>}
              Save
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="organization_name">Organization Name</label>
                <input className="form-control" placeholder="Our Organization" type="text" name="organization_name" id="organization_name"
                  value={team.name}
                  onChange={e => {
                    const val = e.target.value;
                    setTeam(team => {
                      return { ...team, name: val }
                    });
                  }}
                />
              </div>
              <div className="ml-3 mb-2">
                <small>Will be used in a public page's subdomain if custom domain is not provided.</small>
              </div>
              {customDomainFlag &&
                <>
                  <div className="form-group">
                    <label htmlFor="custom_domain">Custom Domain</label>
                    <input className="form-control" placeholder="subdomain.domain.com" type="text" name="custom_domain" id="custom_domain"
                      value={domain.name}
                      onChange={e => {
                        setDomain({...domain, name: e.target.value})
                      }}
                    />
                  </div>
                  <div className="ml-3 mb-2">
                    Follow the instructions everytime the custom domain changes,
                    <b onClick={handleShowInstructions} style={{ cursor: 'pointer' }}> Click Here</b> for instructions
                  </div>

                  <div className="form-group">
                    <label htmlFor="dns_target">DNS Target</label>
                    <div className="input-group">
                      <input className="form-control" placeholder="Auto generated by server" type="text" name="dns_target" id="dns_target" value={domain.dns_target} disabled/>
                      <div className="input-group-append">
                          <button className="btn btn-light btn-border" type="button" onClick={copyToClipboard}><i className="fa fa-clipboard"></i></button>
                      </div>
                    </div>
                  </div>
                </>
              }
              <div className="form-group">
                <label>Logo</label>
                <div className="border rounded bg-grey1 p-2">
                  <div className="d-flex">
                    <img className="img-fluid mx-auto my-2"
                      style={{maxWidth: '200px'}}
                      src={team.logo_url} />
                  </div>
                  <div className='mt-2'>
                    <UploadButton handleChange={setLogoImage} />
                  </div>
                </div>
              </div>
              {featureFlags && featureFlags.includes('white_label') &&
                <div className="form-group">
                  <label htmlFor="organization_name">Choose Organization Themes</label>
                  <ul className="nav nav-tabs">
                      <li className="nav-item">
                          <a className={`nav-link ${activeTab === 'primary' ? 'active' : ''}`}
                            onClick={() => setActiveTab('primary')} >
                            Primary
                          </a>
                      </li>
                      <li className="nav-item">
                          <a className={`nav-link ${activeTab === 'secondary' ? 'active' : ''}`}
                            onClick={() => setActiveTab('secondary')}>
                            Secondary
                          </a>
                      </li>
                      <li className="nav-item">
                          <a className={`nav-link ${activeTab === 'menu_color' ? 'active' : ''}`}
                            onClick={() => setActiveTab('menu_color')}>
                            Menu Text Color
                          </a>
                      </li>
                    </ul>
                    {activeTab === 'primary' &&
                      <SketchPicker
                        className="mt-3"
                        color={myPrimaryColor}
                        onChange={ e => handleColorChange(e)}
                      />
                    }

                    {activeTab === 'secondary' &&
                      <SketchPicker
                        className="mt-3"
                        color={mySecondaryColor}
                        onChange={ e => handleColorChange(e)}
                      />
                    }

                    {activeTab === 'menu_color' &&
                      <SketchPicker
                        className="mt-3"
                        color={team.menu_text_color}
                        onChange={ e => handleColorChange(e)}
                      />
                    }
                </div>
              }
            </div>
            <div className="col-md-6">
              <div className="my-2">
                <strong>Organization settings</strong>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="using_approval"
                  checked={team.using_approvals}
                  onChange={checked => {
                    setTeam(team => ({...team, using_approvals: checked }))
                  }}
                />
                <label htmlFor="using_approvals">Require approval on posts</label>
              </div>
              {team.stripe_uid &&
                <>
                  <div className="my-2">
                    <strong>Payment settings</strong>
                  </div>
                  <div className="form-group">
                    <ToggleSwitch
                      small
                      id="sub_cancelation"
                      checked={team.permit_sub_cancelation}
                      onChange={checked => {
                        setTeam(team => ({...team, permit_sub_cancelation: checked }))
                      }}
                    />
                    <label htmlFor="sub_cancelation">Permit users to cancel subscriptions</label>
                  </div>
                </>
              }
              <div className="my-2">
                <strong>Post Settings</strong>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="post_thread"
                  checked={team.admin_post_thread_public}
                  onChange={checked => {
                    setTeam(team => ({...team, admin_post_thread_public: checked }))
                  }}
                />
                <label htmlFor="post_thread">
                  Public comments for admin posts
                </label>
              </div>
              <div className="my-2">
                <strong>Platform Control</strong>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="show_sponsor_project"
                  checked={team.show_sponsor_project}
                  onChange={checked => {
                    setTeam(team => ({...team, show_sponsor_project: checked }))
                  }}
                />
                <label htmlFor="show_sponsor_project">
                  Show {props.project_plural.toLowerCase()} to {props.donor_plural.toLowerCase()}
                </label>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="show_sponsor_communities"
                  checked={team.show_sponsor_communities}
                  onChange={checked => {
                    setTeam(team => ({...team, show_sponsor_communities: checked }))
                  }}
                />
                <label htmlFor="show_sponsor_communities">
                  Show communities to {props.donor_plural.toLowerCase()}
                </label>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="show_sponsor_reports"
                  checked={team.show_sponsor_reports}
                  onChange={checked => {
                    setTeam(team => ({...team, show_sponsor_reports: checked }))
                  }}
                />
                <label htmlFor="show_sponsor_reports">
                  Show reports to {props.donor_plural.toLowerCase()}
                </label>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="show_sponsor_send_message"
                  checked={team.show_sponsor_send_message}
                  onChange={checked => {
                    setTeam(team => ({...team, show_sponsor_send_message: checked }))
                  }}
                />
                <label htmlFor="show_sponsor_send_message">
                  Show send message button to {props.donor_plural.toLowerCase()}
                </label>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="show_beneficiary_send_message"
                  checked={team.show_beneficiary_send_message}
                  onChange={checked => {
                    setTeam(team => ({...team, show_beneficiary_send_message: checked }))
                  }}
                />
                <label htmlFor="show_beneficiary_send_message">
                  Show send message button to {props.beneficiary_plural.toLowerCase()}
                </label>
              </div>
              <div className="my-2">
                <strong>Notification settings</strong>
              </div>
              <div className="form-group">
                <ToggleSwitch
                  small
                  id="audience_notifications_enabled"
                  checked={team.audience_notifications_enabled}
                  onChange={checked => {
                    setTeam(team => ({...team, audience_notifications_enabled: checked }))
                  }}
                />
                <label htmlFor="audience_notifications_enabled">Enable audience email notifications</label>
                {team.audience_notifications_enabled && (
                  <div className="additional-settings">
                    <ToggleSwitch
                      small
                      id="audience_notifications_preview"
                      checked={team.audience_notifications_preview}
                      onChange={checked => {
                        setTeam(team => ({...team, audience_notifications_preview: checked }))
                      }}
                    />
                    <label htmlFor="audience_notifications_preview">Enable content previews (in emails)</label>
                  </div>
                )}
                <div className="description mb-4">
                  <small>Email sent to audience when post created.</small><br/>
                  <small>If approvals on, post must be approved to trigger.</small><br/>
                  <small>{translations.donors} can individually opt-out.</small>
                </div>
                <div>
                  <label htmlFor="user-invitaions">Mass Invite Functionality</label>
                <div id="user-invitations">
                <button className="btn btn-primary btn-sm mr-2" onClick={handleDonors}>
                    {`Invite All ${team.donor_title}s`}
                </button>

                  {beneficiaryFlag &&
                    <button className="btn btn-secondary btn-sm mr-2" onClick={handleBeneficiaries}>
                      {`Invite All ${team.beneficiary_title}s`}
                    </button>
                  }

                  <button className="btn btn-secondary btn-sm" onClick={handleGroups}>
                    {`Invite Group`}
                  </button>
                  <br/>
                  <div className="mt-2">
                    <small>Send invite to all donors and beneficiaries or to a group of users that have not accepted invite.</small><br/>
                    <small><b>Users without email will be skipped</b></small>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {openDonors &&
          <InputModal 
            saving={saving} handleInvite={(message, userType, group) => handleInvite(message, userType, group)} 
            handleClose={() => setOpenDonors(false)} 
            userType="donor"
          />
        }

        {openBeneficiaries &&
          <InputModal 
            saving={saving} 
            handleInvite={(message, userType, group) => handleInvite(message, userType, group)} 
            handleClose={() => setOpenBeneficiaries(false)} 
            userType="beneficiary" 
          />
        }

        {openGroups &&
          <InputModal 
            saving={saving} 
            handleInvite={(message, userType, group) => handleInvite(message, userType, group)} 
            handleClose={() => setOpenGroups(false)} 
            userType="group"
            groups={props.groups} 
          />
        }

        {openInstructions &&
          <DomainSettingsInfoModal close={() => setOpenInstructions(false)} />
        }
      </div>

      {/* Manager Notification Settings */}
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">
            Manager Notification Settings
          </h4>
        </div>

        <div className="card-body">
          <div className="form-group">
            <div className="row">
              <div className="col-md-6">
                <ToggleSwitch
                  small
                  id="admin_donor_notifications_enabled"
                  checked={team.admin_donor_notifications_enabled}
                  onChange={checked => {
                    setTeam(team => ({...team, admin_donor_notifications_enabled: checked }))
                  }}
                />
                <label htmlFor="admin_donor_notifications_enabled">Enable donor post admin notifications</label>
                <div className="description mb-4">
                  <small>Email sent to admin when post created by donor.</small><br/>
                  <small>If approvals on, admin will receive notice of approval required.</small><br/>
                </div>
              </div>
              <div className="col-md-6">
                <ToggleSwitch
                  small
                  id="admin_beneficiary_notifications_enabled"
                  checked={team.admin_beneficiary_notifications_enabled}
                  onChange={checked => {
                    setTeam(team => ({...team, admin_beneficiary_notifications_enabled: checked }))
                  }}
                />
                <label htmlFor="admin_beneficiary_notifications_enabled">Enable beneficiary post admin notifications</label>
                <div className="description mb-4">
                  <small>Email sent to admin when post created by beneficiary.</small><br/>
                  <small>If approvals on, admin will receive notice of approval required.</small><br/>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <ToggleSwitch
                  small
                  id="admin_field_notification"
                  checked={team.send_admin_field_notifications}
                  onChange={checked => {
                    setTeam(team => ({...team, send_admin_field_notifications: checked }))
                  }}
                />
                <label htmlFor="admin_field_notification">Enable field change notification for admin</label>
                <div className="description mb-4">
                  <small>Email sent to admin when a field that has notifications enabled is changed for any user or project.</small><br/>
                </div>
              </div>
              <div className="col-md-6">
                <ToggleSwitch
                  small
                  id="admin_sponsorship_notifications_enabled"
                  checked={team.admin_sponsorship_notifications_enabled}
                  onChange={checked => {
                    setTeam(team => ({...team, admin_sponsorship_notifications_enabled: checked }))
                  }}
                />
                <label htmlFor="admin_sponsorship_notifications_enabled">Enable sponsorship notification for admin</label>
                <div className="description mb-4">
                  <small>Email sent to admin when a new sponsor sign up to sponsor through public pages.</small><br/>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <ToggleSwitch
                  small
                  id="admin_import_notifications_enabled"
                  checked={team.admin_import_notifications_enabled}
                  onChange={checked => {
                    setTeam(team => ({...team, admin_import_notifications_enabled: checked }))
                  }}
                />
                <label htmlFor="admin_import_notifications_enabled">Enable import notification for admin</label>
                <div className="description mb-4">
                  <small>Email sent to admin once an import request is completed, the CSV used for the import is attached.</small><br/>
                </div>
              </div>
              <div className="col-md-6">
                <ToggleSwitch
                  small
                  id="admin_billing_notifications_enabled"
                  checked={team.admin_billing_notifications_enabled}
                  onChange={checked => {
                    setTeam(team => ({...team, admin_billing_notifications_enabled: checked }))
                  }}
                />
                <label htmlFor="admin_billing_notifications_enabled">Enable billing notification for admin</label>
                <div className="description mb-4">
                  <small>Email sent to admin regarding free trial, successful subscriptions, and add-on purchase.</small><br/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

General.propTypes = {
  team: PropTypes.object
};
